/**
 * Document queue showing all pending documents for review.
 *
 * Features:
 * - Persistent list of documents (not just current session)
 * - Filter by status, type, confidence
 * - "Show low confidence first" toggle for triage
 * - Click to navigate to review page
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  FileText,
  Eye,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  Inbox,
} from "lucide-react";
import { listDocuments, type DocumentMetadata } from "@/lib/extraction-api";
import { useTenant } from "@/hooks/useTenant";
import { supabase } from "@/integrations/supabase/client";

// Document type labels
const documentTypeLabels: Record<string, string> = {
  capital_call: "Capital Call",
  distribution_notice: "Distribution Notice",
  quarterly_account_statement: "Quarterly Statement",
  quarterly_financial_statement: "Financial Statement",
  portfolio_statement_custodian: "Portfolio Statement (Custodian)",
  portfolio_statement_consolidated: "Portfolio Statement (Consolidated)",
  unknown: "Unknown",
};

// Extraction status badge variants
const statusConfig: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ReactNode }> = {
  uploaded: { variant: "secondary", icon: <Clock className="h-3 w-3" /> },
  processing: { variant: "default", icon: <Loader2 className="h-3 w-3 animate-spin" /> },
  completed: { variant: "default", icon: <CheckCircle2 className="h-3 w-3" /> },
  failed: { variant: "destructive", icon: <XCircle className="h-3 w-3" /> },
};

// Review status badge variants
const reviewStatusConfig: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string; icon: React.ReactNode }> = {
  pending: { variant: "outline", label: "Pending", icon: <Clock className="h-3 w-3" /> },
  approved: { variant: "default", label: "Approved", icon: <CheckCircle2 className="h-3 w-3 text-success" /> },
  rejected: { variant: "destructive", label: "Rejected", icon: <XCircle className="h-3 w-3" /> },
};

// Confidence indicator colors
const confidenceConfig: Record<string, { color: string; label: string }> = {
  high: { color: "text-success", label: "High" },
  medium: { color: "text-warning", label: "Medium" },
  low: { color: "text-red-500", label: "Low" },
};


interface DocumentQueueProps {
  /** Optional className */
  className?: string;
}

export function DocumentQueue({ className }: DocumentQueueProps) {
  const navigate = useNavigate();
  const { orgId } = useTenant();

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [confidenceFilter, setConfidenceFilter] = useState<string>("all");
  const [lowConfidenceFirst, setLowConfidenceFirst] = useState(false);

  // Fetch the client record for the user's org
  const { data: clientData } = useQuery({
    queryKey: ["client-for-org", orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const { data, error } = await supabase
        .from("clients")
        .select("id")
        .eq("org_id", orgId)
        .limit(1)
        .single();
      if (error) {
        console.warn("No client found for org:", orgId, error);
        return null;
      }
      return data;
    },
    enabled: !!orgId,
  });

  const clientId = clientData?.id || null;

  // Fetch documents (only when we have a valid clientId)
  const {
    data: documentsResponse,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["documents", clientId],
    queryFn: () => clientId ? listDocuments({ clientId }) : Promise.resolve({ documents: [], total: 0 }),
    refetchInterval: 30000, // Refresh every 30 seconds
    enabled: !!clientId,
  });

  // Filter and sort documents
  const documents = useMemo(() => {
    if (!documentsResponse?.documents) return [];

    let filtered = documentsResponse.documents;

    // Apply status filter
    if (statusFilter !== "all") {
      if (statusFilter === "pending_review") {
        // Pending = completed extraction AND (null or "pending_review" review status)
        filtered = filtered.filter(
          (doc) => doc.status === "completed" && (doc.review_status === "pending_review" || doc.review_status === null)
        );
      } else if (statusFilter === "approved") {
        filtered = filtered.filter((doc) => doc.review_status === "approved");
      } else if (statusFilter === "rejected") {
        filtered = filtered.filter((doc) => doc.review_status === "rejected");
      } else {
        filtered = filtered.filter((doc) => doc.status === statusFilter);
      }
    }

    // Apply confidence filter
    if (confidenceFilter !== "all") {
      filtered = filtered.filter(
        (doc) => doc.classification_confidence === confidenceFilter
      );
    }

    // Sort by confidence (low first) if enabled
    if (lowConfidenceFirst) {
      const confidenceOrder = { low: 0, medium: 1, high: 2, null: 3 };
      filtered = [...filtered].sort((a, b) => {
        const aOrder = confidenceOrder[a.classification_confidence as keyof typeof confidenceOrder] ?? 3;
        const bOrder = confidenceOrder[b.classification_confidence as keyof typeof confidenceOrder] ?? 3;
        return aOrder - bOrder;
      });
    } else {
      // Default: sort by date descending
      filtered = [...filtered].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }

    return filtered;
  }, [documentsResponse, statusFilter, confidenceFilter, lowConfidenceFirst]);

  // Count by confidence for stats
  const stats = useMemo(() => {
    const docs = documentsResponse?.documents || [];
    // Pending = completed extraction AND (null or "pending_review" review status)
    const pendingReview = docs.filter(
      (d) => d.status === "completed" && (d.review_status === "pending_review" || d.review_status === null)
    );
    const approved = docs.filter((d) => d.review_status === "approved");
    const rejected = docs.filter((d) => d.review_status === "rejected");
    return {
      total: docs.length,
      pendingReview: pendingReview.length,
      approved: approved.length,
      rejected: rejected.length,
      lowConfidence: pendingReview.filter((d) => d.classification_confidence === "low").length,
      mediumConfidence: pendingReview.filter((d) => d.classification_confidence === "medium").length,
      highConfidence: pendingReview.filter((d) => d.classification_confidence === "high").length,
    };
  }, [documentsResponse]);

  const handleReview = (documentId: string) => {
    navigate(`/data/review/${documentId}`);
  };

  // Show empty state if no client configured
  if (!clientId && !isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Document Queue</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Inbox className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-1">No client configured</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Your organization needs a client configured to use document extraction.
              Please contact your administrator.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Document Queue</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {stats.pendingReview} pending review
              {stats.lowConfidence > 0 && (
                <span className="text-red-500 ml-2">
                  ({stats.lowConfidence} low confidence)
                </span>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isRefetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Review:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">Confidence:</Label>
            <Select value={confidenceFilter} onValueChange={setConfidenceFilter}>
              <SelectTrigger className="w-[120px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="low-confidence-first"
              checked={lowConfidenceFirst}
              onCheckedChange={setLowConfidenceFirst}
            />
            <Label htmlFor="low-confidence-first" className="text-sm cursor-pointer">
              Show low confidence first
            </Label>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Inbox className="h-10 w-10 mb-3" />
            <p className="font-medium">No documents found</p>
            <p className="text-sm">Upload documents above to get started</p>
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Review</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-[80px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium truncate max-w-[200px]">
                          {doc.filename}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {doc.document_type
                          ? documentTypeLabels[doc.document_type] || doc.document_type
                          : "—"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {doc.classification_confidence ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 cursor-help">
                              {doc.classification_confidence === "low" && (
                                <AlertTriangle className="h-4 w-4 text-red-500" />
                              )}
                              <span
                                className={
                                  confidenceConfig[doc.classification_confidence]?.color ||
                                  "text-muted-foreground"
                                }
                              >
                                {confidenceConfig[doc.classification_confidence]?.label ||
                                  doc.classification_confidence}
                              </span>
                            </div>
                          </TooltipTrigger>
                          {doc.classification_reasoning && (
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">{doc.classification_reasoning}</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {doc.status === "completed" ? (
                        (() => {
                          const reviewKey = doc.review_status === "approved" ? "approved"
                            : doc.review_status === "rejected" ? "rejected"
                            : "pending";
                          const config = reviewStatusConfig[reviewKey];
                          return (
                            <Badge variant={config.variant}>
                              {config.icon}
                              <span className="ml-1">{config.label}</span>
                            </Badge>
                          );
                        })()
                      ) : (
                        <Badge variant={statusConfig[doc.status]?.variant || "secondary"}>
                          {statusConfig[doc.status]?.icon}
                          <span className="ml-1">{doc.status}</span>
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(doc.created_at)}
                    </TableCell>
                    <TableCell>
                      {doc.status === "completed" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReview(doc.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DocumentQueue;
